import React, { useCallback, useEffect, useRef, useState } from 'react';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import { LoginUserStatus, useAuth } from '../../../context/AuthContext';
import { useUpdateMyConfig } from '../../../redux/api/users/usersApi';
import { ALL_NOTIFICATIONS_ENABLED_CONFIG } from '../../../utils/push/all-notifications-enabled-config';
import {
  canUseWebPushInCurrentContext,
  obtainFcmTokenSilentIfPermitted,
  obtainFcmTokenWithPermissionRequest,
} from '../../../utils/push/push-token';
import {
  getStoredPushFcmToken,
  isPushPermissionPromptCompleted,
  setPushPermissionPromptCompleted,
  setStoredPushFcmToken,
} from '../../../utils/push/push-token-storage';
import PushPermissionDialog from './PushPermissionDialog';

const PushPermissionGate = () => {
  const { loginUserStatus } = useAuth();
  const [updateMyConfig] = useUpdateMyConfig();
  const [dialogOpen, setDialogOpen] = useState(false);
  const pushInitStartedRef = useRef(false);

  const syncLocalPushToken = useCallback(async () => {
    try {
      let token = getStoredPushFcmToken();
      if (!token) {
        const silent = await obtainFcmTokenSilentIfPermitted();
        if (silent) {
          token = silent.fcmToken;
          setStoredPushFcmToken(token);
        }
      }
    } catch {
      void 0;
    }
  }, []);

  useEffect(() => {
    if (loginUserStatus !== LoginUserStatus.loggedIn) {
      pushInitStartedRef.current = false;
      return;
    }
    if (pushInitStartedRef.current) {
      return;
    }
    pushInitStartedRef.current = true;

    void (async () => {
      const completed = isPushPermissionPromptCompleted();
      await syncLocalPushToken();

      if (completed) {
        return;
      }

      if (getStoredPushFcmToken()) {
        setPushPermissionPromptCompleted(true);
        return;
      }

      if (Capacitor.isNativePlatform()) {
        const perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'denied') {
          setPushPermissionPromptCompleted(true);
          return;
        }
        if (perm.receive === 'granted') {
          setPushPermissionPromptCompleted(true);
          return;
        }
        setDialogOpen(true);
        return;
      }

      if (!canUseWebPushInCurrentContext()) {
        return;
      }

      if (typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission === 'denied') {
          setPushPermissionPromptCompleted(true);
          return;
        }
        if (Notification.permission === 'granted') {
          setPushPermissionPromptCompleted(true);
          return;
        }
        if (Notification.permission === 'default') {
          setDialogOpen(true);
        }
      }
    })();
  }, [loginUserStatus, syncLocalPushToken]);

  useEffect(() => {
    if (loginUserStatus !== LoginUserStatus.loggedIn) {
      return;
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let active = true;
    let regListener: { remove: () => Promise<void> } | undefined;

    void (async () => {
      const h = await PushNotifications.addListener('registration', (ev) => {
        const token = ev.value?.trim();
        if (!token || !active) {
          return;
        }
        setStoredPushFcmToken(token);
      });
      if (!active) {
        void h.remove();
        return;
      }
      regListener = h;
    })();

    return () => {
      active = false;
      void regListener?.remove();
    };
  }, [loginUserStatus]);

  useEffect(() => {
    if (loginUserStatus !== LoginUserStatus.loggedIn) {
      return;
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const sub = App.addListener('resume', () => {
      void syncLocalPushToken();
    });

    return () => {
      void sub.then((h) => h.remove());
    };
  }, [loginUserStatus, syncLocalPushToken]);

  useEffect(() => {
    if (loginUserStatus !== LoginUserStatus.loggedIn) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const onBecameVisible = () => {
      if (document.visibilityState === 'visible') {
        void syncLocalPushToken();
      }
    };

    window.addEventListener('visibilitychange', onBecameVisible);
    window.addEventListener('focus', onBecameVisible);

    return () => {
      window.removeEventListener('visibilitychange', onBecameVisible);
      window.removeEventListener('focus', onBecameVisible);
    };
  }, [loginUserStatus, syncLocalPushToken]);

  const handleCancel = () => {
    setPushPermissionPromptCompleted(true);
    setDialogOpen(false);
  };

  const handleOk = async () => {
    setDialogOpen(false);
    try {
      const result = await obtainFcmTokenWithPermissionRequest();
      if (result) {
        setStoredPushFcmToken(result.fcmToken);
        await updateMyConfig({
          config: ALL_NOTIFICATIONS_ENABLED_CONFIG.map(({ key, value }) => ({
            key,
            value,
          })),
        })
          .unwrap()
          .catch(() => undefined);
      }
    } catch {
      void 0;
    } finally {
      setPushPermissionPromptCompleted(true);
    }
  };

  if (loginUserStatus !== LoginUserStatus.loggedIn) {
    return null;
  }

  return (
    <PushPermissionDialog
      open={dialogOpen}
      isLoading={false}
      onClose={handleCancel}
      onSubmit={handleOk}
    />
  );
};

export default PushPermissionGate;
